/**
 * Back In Stock Notification Form
 * Custom element for handling out-of-stock product notifications
 */

/* global gtag, fbq, subscribe */

/**
 * Wait for Klaviyo SDK to be ready
 * @param {Function} callback - Function to call when Klaviyo is ready
 * @param {Number} maxWait - Maximum wait time in milliseconds
 */
function waitForKlaviyo(callback, maxWait = 15000) {
  const startTime = Date.now();

  function checkKlaviyo() {
    if (typeof window.klaviyo !== 'undefined' && window.klaviyo) {
      callback();
    } else if (Date.now() - startTime < maxWait) {
      setTimeout(checkKlaviyo, 100);
    } else {
      console.error('[BIS] Klaviyo failed to load within timeout period');
      callback(new Error('Klaviyo SDK timeout'));
    }
  }

  checkKlaviyo();
}

if (!customElements.get('bis-form')) {
  class BISForm extends HTMLElement {
    constructor() {
      super();
      this.debounceTimer = null;
      this.isSubmitting = false;
      this.initialized = false;
    }

    loadTranslations() {
      const dataAttr = this.getAttribute('data-translations');
      let translations = {};

      if (dataAttr) {
        try {
          translations = JSON.parse(dataAttr);
        } catch (e) {
          console.error('Failed to parse translations:', e);
        }
      }

      return {
        emailRequired: translations.emailRequired || 'We\'ll need your email to notify you',
        nameRequired: translations.nameRequired || 'Please enter your first name',
        emailInvalid: translations.emailInvalid || 'Please enter a valid email address',
        phoneInvalid: translations.phoneInvalid || 'Please enter a valid phone number',
        nameInvalid: translations.nameInvalid || 'Please enter a valid name',
        phoneTooShort: translations.phoneTooShort || 'Phone number seems too short',
        tooShort: translations.tooShort || 'Must be at least {min_length} characters',
        validationFailed: translations.validationFailed || 'Please check the form and try again',
        submissionFailed: translations.submissionFailed || 'Something went wrong. Please try again.',
        submitting: translations.submitting || 'Sending...',
        submitButtonDefault: translations.submitButtonDefault || 'Notify Me',
        successNotification: translations.successNotification || 'You\'re on the list!'
      };
    }

    setupEventListeners() {
      this.triggerButton.addEventListener('click', this.openModal.bind(this));
      this.closeButton.addEventListener('click', this.closeModal.bind(this));
      this.cancelButton.addEventListener('click', this.closeModal.bind(this));
      this.overlay.addEventListener('click', this.closeModal.bind(this));
      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      this.handleEscape = (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) {
          this.closeModal();
        }
      };
      document.addEventListener('keydown', this.handleEscape);

      const inputs = this.form.querySelectorAll('input[required]');
      inputs.forEach(input => {
        input.addEventListener('input', this.debounceValidation.bind(this));
        input.addEventListener('blur', () => this.validateField(input));
      });
    }

    setupVariantChangeListener() {
      // Subscribe to theme's PUB_SUB variant change events
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribeVariant = subscribe(window.PUB_SUB_EVENTS.variantChange, (data) => {
          if (data && data.variant) {
            this.updateVariantData(data.variant);
          }
        });
      }
    }

    updateVariantData(variant) {
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }
      if (this.productSkuInput && variant.sku) {
        this.productSkuInput.value = variant.sku;
      }

      // Update price field
      const priceInput = this.form.querySelector('input[name="contact[product_price]"]');
      if (priceInput && variant.price) {
        priceInput.value = variant.price;
      }

      if (this.bodyInput) {
        const productTitle = document.querySelector('.product__title')?.textContent.trim() || '';
        const variantTitle = variant.title || '';
        const messageTemplate = this.bodyInput.getAttribute('value') || 'Back in stock notification request for {product_title} - {variant_title}';
        this.bodyInput.value = messageTemplate.replace('{product_title}', productTitle).replace('{variant_title}', variantTitle);
      }

      // Toggle wrapper and buttons based on variant availability
      const isAvailable = this.getVariantAvailability(variant);
      this.toggleWrapper(!isAvailable); // Show wrapper when OUT of stock
      this.toggleAddToCart(isAvailable); // Show add to cart when IN stock
    }

    getVariantAvailability(variant) {
      // Priority 1: Use variant data directly if available
      if (variant && variant.available !== undefined) {
        return variant.available;
      }

      // Priority 2: Query product JSON data from DOM
      try {
        const productJson = document.querySelector('[data-product-json]');
        if (productJson && variant && variant.id) {
          const product = JSON.parse(productJson.textContent);
          const variantData = product.variants.find(v => String(v.id) === String(variant.id));
          if (variantData) {
            return variantData.available;
          }
        }
      } catch (e) {
        console.warn('Failed to parse product JSON:', e);
      }

      // Priority 3: Check variant input disabled state
      const variantInput = document.querySelector('input[name="id"].product-variant-id');
      if (variantInput && variant && String(variantInput.value) === String(variant.id)) {
        return !variantInput.disabled;
      }

      // Default to available if we can't determine
      return true;
    }

    toggleWrapper(show) {
      // Don't manage wrapper for bundle products
      const isBundleProduct = document.querySelector('[data-product-id]')?.dataset.bundleEnabled === 'true';
      if (isBundleProduct) return;

      if (this.wrapper) {
        this.wrapper.style.display = show ? 'block' : 'none';
      }
    }

    toggleAddToCart(show) {
      // Find add to cart button
      const addToCartButton = document.querySelector('.product-form__submit');
      if (addToCartButton) {
        addToCartButton.style.display = show ? '' : 'none';
      }

      // Find and toggle dynamic checkout wrapper
      const dynamicCheckout = document.querySelector('.shopify-payment-button');
      if (dynamicCheckout) {
        dynamicCheckout.style.display = show ? '' : 'none';
      }
    }

    openModal() {
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      this.trackEvent('bis_form_viewed', this.getEventProperties());

      this.trapFocus();

      const firstInput = this.form.querySelector('input[type="text"]');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }
    }

    closeModal() {
      const wasFormFilled = this.form.querySelector('input[name="contact[email]"]')?.value.length > 0;
      // Track abandonment only if form was filled but NOT successfully submitted
      // successMessage.hidden = true means form was NOT successfully submitted
      if (wasFormFilled && this.successMessage.hidden) {
        this.trackEvent('bis_form_cancelled', this.getEventProperties({ form_filled: wasFormFilled }));
      }

      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.triggerButton.focus();
      this.resetForm();
    }

    trapFocus() {
      const focusableElements = this.modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      this.modal.addEventListener('keydown', handleTabKey);
    }

    debounceValidation(event) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.validateField(event.target);
      }, 300);
    }

    validateField(input) {
      const fieldWrapper = input.closest('.field');
      if (!fieldWrapper) return true;

      const errorDiv = fieldWrapper.querySelector('.field__error');
      fieldWrapper.classList.remove('field--error');
      input.setAttribute('aria-invalid', 'false');

      if (errorDiv) {
        errorDiv.hidden = true;
        errorDiv.textContent = '';
      }

      if (!input.validity.valid) {
        fieldWrapper.classList.add('field--error');
        input.setAttribute('aria-invalid', 'true');
        this.showFieldError(input);
        return false;
      }

      return true;
    }

    showFieldError(input) {
      let errorMessage = '';

      if (input.validity.valueMissing) {
        if (input.type === 'email') {
          errorMessage = this.translations.emailRequired;
        } else if (input.name === 'contact[name]') {
          errorMessage = this.translations.nameRequired;
        }
      } else if (input.validity.patternMismatch) {
        if (input.type === 'email') {
          errorMessage = this.translations.emailInvalid;
        } else if (input.type === 'tel') {
          errorMessage = this.translations.phoneInvalid;
        } else if (input.name === 'contact[name]') {
          errorMessage = this.translations.nameInvalid;
        }
      } else if (input.validity.tooShort) {
        if (input.type === 'tel') {
          errorMessage = this.translations.phoneTooShort;
        } else {
          errorMessage = this.translations.tooShort.replace('{min_length}', input.minLength);
        }
      }

      if (errorMessage) {
        const fieldWrapper = input.closest('.field');
        const errorDiv = fieldWrapper?.querySelector('.field__error');
        if (errorDiv) {
          errorDiv.textContent = errorMessage;
          errorDiv.hidden = false;
        }
        this.showError(errorMessage);
      }
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting) return;

      const inputs = this.form.querySelectorAll('input[required]');
      let isValid = true;

      inputs.forEach(input => {
        if (!this.validateField(input)) {
          isValid = false;
        }
      });

      if (!isValid) {
        this.showError(this.translations.validationFailed);
        this.trackEvent('bis_form_error', this.getEventProperties({ error_type: 'validation' }));
        return;
      }

      this.isSubmitting = true;
      this.setSubmitting(true);
      this.hideMessages();

      // Get values for analytics and Klaviyo
      const email = this.form.querySelector('input[name="contact[email]"]')?.value;
      const name = this.form.querySelector('input[name="contact[name]"]')?.value;
      const phone = this.form.querySelector('input[name="contact[phone]"]')?.value;
      const productId = this.form.querySelector('input[name="contact[product_id]"]')?.value;
      const variantId = this.form.querySelector('input[name="contact[variant_id]"]')?.value;
      const productSku = this.form.querySelector('input[name="contact[product_sku]"]')?.value;
      const productPrice = this.form.querySelector('input[name="contact[product_price]"]')?.value;

      // Send to Klaviyo and wait for completion
      try {
        await this.sendToKlaviyo({
          email,
          name,
          phone,
          productId,
          variantId,
          productSku,
          productPrice
        });
      } catch (error) {
        console.warn('[BIS] Klaviyo submission failed, but form will still submit to Shopify:', error);
        // Continue with form submission even if Klaviyo fails
      }

      // Track submission
      this.trackEvent('bis_form_submitted', this.getEventProperties({
        has_phone: phone?.length > 0,
        customer_email: email,
        customer_name: name
      }));

      // Create hidden iframe for submission (prevents page redirect)
      const iframe = document.createElement('iframe');
      iframe.name = 'bis-submit-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      // Set form target to iframe
      this.form.target = 'bis-submit-frame';

      // Show success and close after submission
      this.showSuccess();

      // Submit form to iframe
      this.form.submit();

      // Close modal after delay
      setTimeout(() => {
        this.closeModal();
        // Clean up iframe
        document.body.removeChild(iframe);
        // Reset form target
        this.form.target = '';
      }, 3000);

      this.isSubmitting = false;
      this.setSubmitting(false);
    }

    setSubmitting(isSubmitting) {
      if (isSubmitting) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.submitButton.textContent = this.translations.submitting;
      } else {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('loading');
        this.submitButton.textContent = this.submitButton.dataset.originalText || this.translations.submitButtonDefault;
      }
    }

    showError(message) {
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
      this.successMessage.hidden = true;
    }

    showSuccess() {
      this.successMessage.hidden = false;
      this.errorMessage.hidden = true;
      this.submitButton.classList.add('bis-form__submit--success');
      this.submitButton.textContent = this.translations.successNotification;
    }

    hideMessages() {
      this.errorMessage.hidden = true;
      this.successMessage.hidden = true;
    }

    resetForm() {
      this.form.reset();
      this.hideMessages();
      this.submitButton.classList.remove('bis-form__submit--success');
      this.submitButton.textContent = this.submitButton.dataset.originalText || this.translations.submitButtonDefault;
      const fields = this.form.querySelectorAll('.field');
      fields.forEach(field => {
        field.classList.remove('field--error');
        const input = field.querySelector('input');
        if (input) {
          input.setAttribute('aria-invalid', 'false');
        }
        const errorDiv = field.querySelector('.field__error');
        if (errorDiv) {
          errorDiv.hidden = true;
          errorDiv.textContent = '';
        }
      });
    }

    getEventProperties(additionalProps = {}) {
      const productId = this.form.querySelector('input[name="contact[product_id]"]')?.value;
      const variantId = this.variantIdInput?.value;
      const productSku = this.productSkuInput?.value;

      const productTitle = document.querySelector('.product__title')?.textContent.trim() || '';

      const variantTitle = this.bodyInput?.value.match(/- (.+)$/)?.[1] || '';

      // Get price from hidden form field (most reliable)
      let productPrice = this.form.querySelector('input[name="contact[product_price]"]')?.value;

      // Fallback: try to get from product JSON
      if (!productPrice) {
        try {
          const productJson = document.querySelector('[data-product-json]');
          if (productJson) {
            const product = JSON.parse(productJson.textContent);
            const variant = product.variants.find(v => String(v.id) === String(variantId));
            if (variant) {
              productPrice = String(variant.price);
            }
          }
        } catch (e) {
          console.warn('Failed to get price from product JSON:', e);
        }
      }

      const userLoggedIn = window.wrPageContext?.customerId !== null && window.wrPageContext?.customerId !== undefined;

      return {
        product_id: productId || null,
        variant_id: variantId || null,
        product_sku: productSku || null,
        product_title: productTitle || null,
        variant_title: variantTitle || null,
        product_price: productPrice || null,
        user_logged_in: userLoggedIn,
        currency: window.wrPageContext?.currency || 'USD',
        page_path: window.location.pathname,
        timestamp: new Date().toISOString(),
        ...additionalProps
      };
    }

    sendToKlaviyo(data) {
      return new Promise((resolve, reject) => {
        // Wait for Klaviyo to be ready
        waitForKlaviyo((error) => {
          if (error) {
            console.error('[BIS] Klaviyo not available:', error);
            reject(error);
            return;
          }

          try {
            // Get product title and variant title from page
            const productTitle = document.querySelector('.product__title')?.textContent.trim() || '';
            const variantTitle = this.bodyInput?.value.match(/- (.+)$/)?.[1] || '';

            // Format phone number to E.164 if provided
            let formattedPhone = '';
            if (data.phone && data.phone.length > 0) {
              // Basic E.164 formatting for US numbers
              formattedPhone = data.phone.replace(/\D/g, '');
              if (formattedPhone.length === 10) {
                formattedPhone = '+1' + formattedPhone;
              } else if (formattedPhone.length === 11 && formattedPhone.startsWith('1')) {
                formattedPhone = '+' + formattedPhone;
              }
            }

            // Identify the customer profile in Klaviyo with list subscription
            const profileData = {
              $email: data.email,
              $first_name: data.name || '',
              // Add custom properties to trigger list addition via Klaviyo flow/segment
              BackInStockSubscriber: 'true',
              BackInStockSignupDate: new Date().toISOString(),
              LastBackInStockProduct: productTitle,
              LastBackInStockVariant: variantTitle,
              LastBackInStockProductURL: window.location.href
            };

            if (formattedPhone) {
              profileData.$phone_number = formattedPhone;
            }

            console.log('[BIS] Identifying profile with data:', profileData);
            window.klaviyo.push(['identify', profileData]);

            // Track the Back in Stock Request event (this will also identify the profile)
            const eventData = {
              $email: data.email,
              $first_name: data.name || '',
              ProductID: data.productId,
              VariantID: data.variantId,
              ProductSKU: data.productSku,
              ProductName: productTitle,
              VariantName: variantTitle,
              ProductPrice: data.productPrice ? (parseInt(data.productPrice) / 100).toFixed(2) : '0.00',
              ProductURL: window.location.href,
              RequestDate: new Date().toISOString(),
              Currency: window.wrPageContext?.currency || 'USD'
            };

            if (formattedPhone) {
              eventData.$phone_number = formattedPhone;
            }

            window.klaviyo.push(['track', 'Back in Stock Request', eventData]);

            console.log('[BIS] Successfully sent to Klaviyo:', {
              profile: profileData,
              event: eventData
            });

            resolve();
          } catch (error) {
            console.error('[BIS] Error sending to Klaviyo:', error);
            reject(error);
          }
        });
      });
    }

    trackEvent(eventName, properties) {
      // Manual gtag removed — GA4 is now handled by the Google & YouTube app.
      // if (typeof gtag === 'function') {
      //   gtag('event', eventName, properties);
      // }

      if (typeof fbq === 'function') {
        fbq('trackCustom', eventName, properties);
      }

      if (window.Shopify?.theme?.role === 'development') {
        console.log('[BIS Analytics]', eventName, properties);
      }
    }

    connectedCallback() {
      // Only initialize once
      if (this.initialized) return;
      this.initialized = true;

      // Query for all elements now that DOM is ready
      this.triggerButton = this.querySelector('.bis-trigger-button');
      this.modal = this.querySelector('.bis-form__modal');
      this.overlay = this.querySelector('.bis-form__overlay');
      this.closeButton = this.querySelector('.bis-form__close');
      this.cancelButton = this.querySelector('.bis-form__cancel');
      this.form = this.querySelector('.bis-form__form');
      this.submitButton = this.querySelector('.bis-form__submit');
      this.errorMessage = this.querySelector('.bis-form__error-message');
      this.successMessage = this.querySelector('.bis-form__success-message');
      this.variantIdInput = this.querySelector('.bis-variant-id');
      this.productSkuInput = this.querySelector('.bis-product-sku');
      this.bodyInput = this.querySelector('.bis-form__body');

      // Store reference to wrapper for visibility toggling
      this.wrapper = document.querySelector('[data-back-in-stock-wrapper]');

      // Load translations
      this.translations = this.loadTranslations();

      // Store original submit button text
      if (this.submitButton && !this.submitButton.dataset.originalText) {
        this.submitButton.dataset.originalText = this.submitButton.textContent;
      }

      // Cache focusable elements
      if (this.modal) {
        this.focusableElements = this.modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
      }

      // Setup event listeners
      this.setupEventListeners();
      this.setupVariantChangeListener();
    }

    disconnectedCallback() {
      if (this.handleEscape) {
        document.removeEventListener('keydown', this.handleEscape);
      }

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Unsubscribe from variant change events
      if (this.unsubscribeVariant) {
        this.unsubscribeVariant();
      }
    }
  }

  customElements.define('bis-form', BISForm);
}
